import React, {useState} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DeviceSetupCard from './DeviceSetupCard';
import { useSelector, useDispatch } from 'react-redux';
import QuestionnaireSetupCard from './QuestionnaireSetupCard';
import styles from './ManagementDropdownCard.module.css';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import PerinPatchSetupCard from './PerinPatchSetupCard';
import { customStyles } from './AccountManagement/CustomStyles';
import Select from 'react-select';
import PerinHealthModal from './PerinHealthModel';
import { openModal, closeModal } from './PerinHealthModelSlice';

const ManagementDropdownCard = (props) => {

    const {
        open=false,
        cardInfo, // id, name,type, and modalities for group device if cardType is Device
        removeHandler,
        updateHandler, // update whole device info like startTime, interval, frequency
        patientEnrollment=false,
        dataList, //list of current devices, questionnaires, or content in program/enrollment, questionnaire, or content
        setModality, //setModality function to update goal, upper+lower limit, passed in as param to differentiate between program/enrollment 
        errorShowing,
        disabled=false,
        viewOnly=false,
        modalityList=[], // current modality state for devices in program/enrollment/patient
        allDevices=[], // all available group devices to select from,
        setSerialNumber=()=>{}
    } = props;

    const [openCardToggle, setOpenCardToggle] = useState(open);
    const toggleCardOpen = () => {
        setOpenCardToggle(!openCardToggle);
    };

    const dispatch = useDispatch();
    const patientId = useSelector((state) => state.enrollPatientProgram.patient.id);
    const savedSerialNumbers = useSelector((state) => state.savedPatientModalities.patientRecentSerialNumbers[patientId]) ?? {};
    const [possibleNewSN, setPossibleNewSN] = useState({})

    const title = cardInfo.title;
    const id = cardInfo.id;
    const isDevice = cardInfo.type === "Device";
    const isQuestionnaire = cardInfo.type === "Questionnaire";
    const isContent = cardInfo.type === "Content";

    const serialNumber = patientEnrollment ? dataList.find((item)=> (item.deviceId === id))?.serialNumber : "";
    const macAddress = patientEnrollment ? dataList.find((item)=> (item.deviceId === id))?.macAddress : "";
    const serialNumberId = patientEnrollment ? dataList.find((item)=> (item.deviceId === id))?.serialNumberId : "";

    const cardWidth = isDevice ? "1018px" : "740px";

    const {groupId} = useSelector((state)=>state.addProgram.program);
    const enrollmentGroups = useSelector((state) => state.enrollPatientProgram.enrollment.groups) ?? [];
    const filteredDevices = patientEnrollment ? allDevices.filter((device) =>
        enrollmentGroups.some((group) => group.groupId === device.groupId)
    ) : allDevices.filter((device)=>device.groupId === groupId);

    const hasMultipleGroups = new Set(enrollmentGroups.map((g) => g.groupId)).size > 1;
    const groupIdToName = new Map(
        enrollmentGroups
            .filter((g) => g?.groupId != null && g?.groupName)
            .map((g) => [g.groupId, g.groupName])
    );

    const matchingGroupDevicesForThisPatient = filteredDevices.filter(
        (d) => d.deviceId === id && enrollmentGroups.some((g) => g.groupId === d.groupId)
    );
    const showGroupContextForThisDevice = hasMultipleGroups && matchingGroupDevicesForThisPatient.length > 1;

    const resolveSelectedSerialGroup = () => {
        if (!patientEnrollment) return { groupId: null, groupName: null };
        if (!serialNumberId) return { groupId: null, groupName: null };
        const match = matchingGroupDevicesForThisPatient.find((device) =>
            Array.isArray(device.serialNumbers) &&
            device.serialNumbers.some((sn) => sn?.id === serialNumberId)
        );
        const groupId = match?.groupId ?? null;
        return { groupId, groupName: groupId != null ? groupIdToName.get(groupId) ?? null : null };
    };

    const selectedSerialGroup = resolveSelectedSerialGroup();
    const selectedSerialSuffix = showGroupContextForThisDevice
        ? ` (${selectedSerialGroup.groupName ?? selectedSerialGroup.groupId ?? "Unknown Group"})`
        : "";
    const selectedSerialLabel = serialNumber ? `${serialNumber}${selectedSerialSuffix}` : "";

    const availableSerialNumbers = (() => {
        const matchingDevices = matchingGroupDevicesForThisPatient.filter((item) => item.deviceId === id);
        const unassigned = [];
        const seenSerialIds = new Set();
        for (const device of matchingDevices) {
            if (!Array.isArray(device.serialNumbers)) continue;
            for (const num of device.serialNumbers) {
                if (num.isAssigned && num.id !== serialNumberId) continue;
                const groupKey = showGroupContextForThisDevice ? `|g:${device.groupId ?? "null"}` : "";
                const dedupeKey = num?.id != null ? `id:${num.id}${groupKey}` : `sn:${num.serialNumber}${groupKey}`;
                if (seenSerialIds.has(dedupeKey)) continue;
                seenSerialIds.add(dedupeKey);
                unassigned.push({
                    ...num,
                    groupId: device.groupId,
                    groupName: groupIdToName.get(device.groupId),
                });
            }
        }
        return unassigned;
    })();
    const formattedSerialNumbers = availableSerialNumbers.map((item)=>{
        const groupSuffix = showGroupContextForThisDevice ? ` (${item.groupName ?? item.groupId ?? "Unknown Group"})` : "";
        return {
            label: `${item.serialNumber}${groupSuffix}`,
            value: {"serialNumber": item.serialNumber, "serialNumberId":item.id, "groupId": item.groupId}
        }
    });

    const selectedSerialOption = formattedSerialNumbers.find((opt) => {
        const optId = opt?.value?.serialNumberId;
        if (optId == null || serialNumberId == null) return false;
        if (Number(optId) !== Number(serialNumberId)) return false;
        if (!showGroupContextForThisDevice) return true;
        return opt?.value?.groupId === selectedSerialGroup.groupId;
    }) ?? null;

    const serialNumberInfoTooltip = (
        <Tooltip id="hub-info-tooltip">
            {title === "Perin Patch" ?
                "The Serial Number for the Perin Patch can be found on the outside of the box, next to the barcode on the back, bottom right-hand side of the box." :
                "The Serial Number for RPM devices can be found on the device or its packaging, usually near the barcode."
            }
        </Tooltip>
    );

    const handleSerialNumber = (selected) => {
        if (savedSerialNumbers.hasOwnProperty(id)) {
            if (savedSerialNumbers[id].serialNumberId !== Number(selected.value.serialNumberId)) {
                dispatch(
                    openModal({
                        activeModalId: `ManagementDropdownCard-${title}-${id}`,
                        header: "Reassign Serial Number?",
                        message: `All of patient's current enrollments with this device (${title}) will be reassigned`,
                        buttons: [
                            { label: 'Confirm', variant: 'primary', onClickKey: "Add" },
                            { label: 'Cancel', variant: 'secondary', onClickKey: "Cancel" },
                        ],
                    })
                );
                setPossibleNewSN({id:id, serial:selected.value.serialNumber, serialId: Number(selected.value.serialNumberId)});
                return;
            }
        }
        setSerialNumber({id:id, serial:selected.value.serialNumber, serialId: Number(selected.value.serialNumberId)})
    };

    const handleModalOptions = (onClickKey) => {
        if (onClickKey === "Add") {
            dispatch(closeModal());
            setSerialNumber(possibleNewSN);
        } else {
            dispatch(closeModal());
        }
    };

    return (
        <div style={{width:cardWidth}} className={styles.DropdownContainer}>
            <div className={openCardToggle ? styles.CardTitleOpened : styles.CardTitleClosed}>
                <div style={{display: "flex", gap: "10px", alignItems: "center", width: "100%"}}>
                    <div onClick={toggleCardOpen}>
                        <FontAwesomeIcon icon={`fa-solid fa-angle-${openCardToggle ? 'down' : 'right'}`}/>
                    </div>
                    <span className={styles.CardTitleLabel}>{title}</span>
                    {patientEnrollment && isDevice && (
                        viewOnly ?
                        <>
                        <div>{`SN: ${selectedSerialLabel},`}</div>
                        <div>{`Mac Address: ${macAddress ? macAddress : 'none'}`}</div>
                        </>
                        :
                        <>
                            <div style={{position: "relative", width: "158px"}}>
                                <span className={"floating-label"} style={(errorShowing.showing && serialNumber === "")?{color:"#DC3545",zIndex:"1"}:{zIndex:"1"}}>Serial Number</span>
                                    <Select
                                        inputId="enrollment-serial-number-selector"
                                        options={formattedSerialNumbers}
                                        styles={{
                                            ...customStyles.popup,
                                            container: (provided) => ({
                                                ...provided,
                                                width: '100%',
                                            }),
                                            control: (provided, {isDisabled }) => ({
                                                ...customStyles.popup.control(provided, { isDisabled }),
                                                borderColor: (errorShowing.showing && serialNumber === "") ? '#DC3545' : '#686869',
                                                '&:hover': {
                                                    borderColor: (errorShowing.showing && serialNumber === "") ? '#DC3545' : '#686869',
                                                }
                                            }),
                                        }}
                                        value={selectedSerialOption}
                                        placeholder=''
                                        isSearchable={true}
                                        onChange={handleSerialNumber}
                                    />
                            </div>
                            <OverlayTrigger placement="right" overlay={serialNumberInfoTooltip}>
                                <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{ marginLeft: "10px", color: '#686869' }} />
                            </OverlayTrigger>
                        </>
                    )}
                    {!viewOnly &&
                        <div onClick={()=>{
                                if (disabled) {
                                    return;
                                } else {
                                    removeHandler(id)
                                }
                            }} 
                            style={{float:"right", marginLeft:"auto", cursor:"pointer"}}
                        >
                            <FontAwesomeIcon icon="fa-solid fa-trash-can" style={{color:"#4F4F4F"}}/>
                        </div>
                    }
                </div>
                {errorShowing.serialNumberShowing && serialNumber === "" &&
                    <div className="page-error">
                        Please select a valid serial number.
                    </div>
                }
            </div>
            <div className={styles.OpenCardContainer} 
                style={isDevice ? {height:"auto"} : {height:"166px"}}
                hidden={!openCardToggle}
            >
                {isDevice && (
                    id === 1 ?
                    <PerinPatchSetupCard 
                        id={id} 
                        modalities={cardInfo.modalities} 
                        patientEnrollment={patientEnrollment} 
                        setModality={setModality} 
                        modalityState={modalityList}
                        errorShowing={errorShowing}
                        disabled={disabled}
                        viewOnly={viewOnly}
                        allDevices={allDevices}
                    />
                    :
                    <DeviceSetupCard 
                        id={id} 
                        modalities={cardInfo.modalities} 
                        dataList={dataList} 
                        setCardData={updateHandler} 
                        patientEnrollment={patientEnrollment} 
                        setModality={setModality} 
                        modalityState={modalityList}
                        errorShowing={errorShowing}
                        disabled={disabled}
                        viewOnly={viewOnly}
                        allDevices={allDevices}
                    />
                )
                }
                {(isContent || isQuestionnaire) &&
                    <QuestionnaireSetupCard 
                        isQuestionnaire={isQuestionnaire}
                        id={id} 
                        dataList={dataList} 
                        setCardData={updateHandler}
                        errorShowing={errorShowing}
                        disabled={disabled}
                        viewOnly={viewOnly}
                    />
                }               
            </div>
            <PerinHealthModal
                modalId={`ManagementDropdownCard-${title}-${id}`}
                handleButtonClick={handleModalOptions}
            />
        </div>
    )
}

export default ManagementDropdownCard;

